import { borderFlagsBottom, borderFlagsTop, directionHorizontal, directionVertical } from './table';
import TableBorder from './table-border';
import TableCell from './table-cell';
import TableQuery from './table-query';

// A utility function to work with table resizing operations
// While enabling table edit mode, an instance of this is created
// and it is deleted on quitting table edit mode
export default class TableResize {
  constructor (tableElement, table) {
    // Stop resizing on mouse up
    this.tableElement = tableElement;
    this.table = table;

    this.callbackDocumentMouseUpHandler = null;
    this.callbackDocumentMouseMoveHandler = null;
  }

  callbackDocumentMouseUp () {
    if (this.isMouseDown) {
      this.table.renderAllTexts();
      this.table.updateTableRect(null, true);

      INKIVE.DesignFacade.trigger('table.update');

      if (this.tableElement.classList.contains('selected')) {
        INKIVE.Table.buildCornerGrip(this.tableElement);
        const selector = svgedit.select.getSelectorManager().requestSelector(this.tableElement);
        selector?.resize();
        selector?.showGrips(true);
        this.table.tableSelectionInstance.toggleTableSelectorBorder();
      }
    }

    this.isMouseDown = false;
    this.currentBorderElement = undefined;
  }

  // Select a border on mouse click (to be moved)
  callbackBorderMouseDown (event) {
    const canvas = INKIVE.DesignFacade.canvas;
    this.isMouseDown = true;
    // The selected border
    this.currentBorderElement = event.target;
    // Initial clicking point
    this.mouseDownPoint = [event.pageX, event.pageY];
    this.currentZoom = canvas.getZoom();
    canvas.getMode() !== 'table-edit' && canvas.setMode('table-edit');
    // Cache data about the selected border
    this.borderData = TableQuery.getBorderData(this.currentBorderElement, true, false, false);

    // If the selected border is vertical
    this.isVertical = TableQuery.isVerticalBorder(this.currentBorderElement);

    INKIVE.Table.removeCornerGrip();
    const selector = canvas.selectorManager.selectors[0];
    selector?.showGrips(false);
    selector?.toggleSelectorRect(false);
  }

  // Slide the selected border while resizing the associated cells and borders
  callbackDocumentMouseMove (event) {
    if (!this.isMouseDown || !this.currentBorderElement) return;

    const that = this;

    // Helper function to slide or resize a border line
    function changeBorder (query, action, delta, how) {
      const borderElements = that.tableElement.querySelectorAll(query);

      borderElements.forEach(borderElement => {
        const border = new TableBorder();
        border.fromElement(borderElement);

        action === 'slide'
          ? border.slideBy(delta, how)
          : border.resizeFrom(delta, how);
      });
    }

    // Collects cells based on the passed query and returns the array
    // Returns null, if one of the cells does not allow resizing
    function collectCells (query, delta, how) {
      const cellElements = that.tableElement.querySelectorAll(query);
      const cells = [];

      for (let i = 0; i < cellElements.length; i++) {
        const tableCell = new TableCell();
        tableCell.fromElement(cellElements[i]);
        // If this cell can be resized by delta
        if (!tableCell.confirmSizeChange(delta, how)) return null;
        cells.push(tableCell);
      }

      return cells;
    }

    // Current mouse position on the document
    const mousePoint = [event.pageX, event.pageY];

    if (this.isVertical) {
      const dx = (mousePoint[0] - this.mouseDownPoint[0]) / this.currentZoom;

      // Collect cells on the left hand
      const leftCells = collectCells(`g.table-cell.index-c-${this.borderData.columnIndex - 1}`, dx, directionHorizontal);
      if (leftCells === null) return;

      // Collect cells on the right hand
      const rightCells = collectCells(`g.table-cell.index-c-${this.borderData.columnIndex}`, -dx, directionHorizontal);
      if (rightCells === null) return;

      // Resize cells on the left hand
      leftCells.forEach(leftCell => TableQuery.isMergeMaster(leftCell.cellElement) || leftCell.resize(dx, directionHorizontal));

      // Resize and move cells on the right hand
      rightCells.forEach(rightCell => {
        rightCell.move(dx, directionHorizontal);
        TableQuery.isMergeMaster(rightCell.cellElement) || rightCell.resize(-dx, directionHorizontal);
      });

      // Select and move vertical borders
      changeBorder(`line.table-border-v.index-c-${this.borderData.columnIndex}`, 'slide', dx, directionHorizontal);

      // Select and resize horizontal borders on the left hand
      this.borderData.columnIndex > 0 && changeBorder(`line.table-border-h.index-c-${this.borderData.columnIndex - 1}`, 'resize', dx, 'r');

      // Select and resize horizontal borders on the right hand
      changeBorder(`line.table-border-h.index-c-${this.borderData.columnIndex}`, 'resize', dx, 'l');
    } else {
      const dy = (mousePoint[1] - this.mouseDownPoint[1]) / this.currentZoom;

      // Collect cells on top
      const topCells = collectCells(`g.table-cell.index-r-${this.borderData.rowIndex - 1}`, dy, directionVertical);
      if (topCells === null) return;

      // Collect cells on bottom
      const bottomCells = collectCells(`g.table-cell.index-r-${this.borderData.rowIndex}`, -dy, directionVertical);
      if (bottomCells === null) return;

      // Resize cells on top
      topCells.forEach(topCell => TableQuery.isMergeMaster(topCell.cellElement) || topCell.resize(dy, directionVertical));

      // Resize and move cells on bottom
      bottomCells.forEach(bottomCell => {
        bottomCell.move(dy, directionVertical);
        TableQuery.isMergeMaster(bottomCell.cellElement) || bottomCell.resize(-dy, directionVertical);
      });

      // Select and move horizontal borders
      changeBorder(`line.table-border-h.index-r-${this.borderData.rowIndex}`, 'slide', dy, directionVertical);

      // Select and resize vertical borders on top side (transform point is derived from bottom borders)
      this.borderData.rowIndex > 0 && changeBorder(`line.table-border-v.index-r-${this.borderData.rowIndex - 1}`, 'resize', dy, borderFlagsBottom);

      // Select and resize vertical borders on bottom side (transform point is derived from top borders)
      changeBorder(`line.table-border-v.index-r-${this.borderData.rowIndex}`, 'resize', dy, borderFlagsTop);
    }

    this.resizeMergedCells();

    this.mouseDownPoint = [event.pageX, event.pageY];

    this.table.renderAllTexts();
    this.table.updateTableRect(null, true);
  }

  // Calculate size of all merged cells
  resizeMergedCells (rowIndex, delta) {
    const masterCellElements = this.tableElement.querySelectorAll('g.table-cell-max');

    for (const masterCellElement of masterCellElements) {
      const m = TableQuery.getMergeId(masterCellElement);
      const slaveCellElements = this.tableElement.querySelectorAll(`g.table-cell-min.m-${m}`);

      let r = -1;
      let c = -1;
      let bottomRightCellData = null;

      slaveCellElements.forEach(slaveCellElement => {
        const cellData = TableQuery.getCellData(slaveCellElement, true, true, true, false, true);

        if (cellData.rowIndex >= r && cellData.columnIndex >= c) {
          r = cellData.rowIndex;
          c = cellData.columnIndex;
          bottomRightCellData = { ...cellData };
        }
      });

      if (!bottomRightCellData) continue;

      if (rowIndex && rowIndex > bottomRightCellData.rowIndex) continue;

      const masterCellData = TableQuery.getCellData(masterCellElement, true, true, true, false, true);

      const masterCellRectElement = masterCellElement.querySelector('rect');

      const horizontalDiff = bottomRightCellData.x - masterCellData.x + bottomRightCellData.width;
      const verticalDiff = bottomRightCellData.y - masterCellData.y + bottomRightCellData.height;

      if (masterCellData.angle === 90 || masterCellData.angle === 270) {
        const factor = masterCellData.angle === 270 ? -1 : 1;
        if (delta) {
          masterCellRectElement.setAttribute('x', Number(masterCellRectElement.getAttribute('x')) + factor * delta);
        }
        masterCellRectElement.setAttribute('width', verticalDiff);
        masterCellRectElement.setAttribute('height', horizontalDiff);
      } else {
        masterCellRectElement.setAttribute('width', horizontalDiff);
        masterCellRectElement.setAttribute('height', verticalDiff);
      }
    }
  }

  // Add events to the document so borders can be moved by mouse
  enableResizing () {
    this.callbackDocumentMouseUpHandler = this.callbackDocumentMouseUp.bind(this);
    this.callbackDocumentMouseMoveHandler = this.callbackDocumentMouseMove.bind(this);

    document.addEventListener('mouseup', this.callbackDocumentMouseUpHandler);
    document.addEventListener('mousemove', this.callbackDocumentMouseMoveHandler);
  }

  // Remove events added to the document (to move borders by mouse)
  disableResizing () {
    document.removeEventListener('mouseup', this.callbackDocumentMouseUpHandler);
    document.removeEventListener('mousemove', this.callbackDocumentMouseMoveHandler);
  }

  // Allow this border to be moved by mouse
  registerBorder (borderElement) {
    borderElement.onmousedown = this.callbackBorderMouseDown.bind(this);
  }

  // Remove attached events to the give border
  unregisterBorder (borderElement) {
    const clone = borderElement.cloneNode(true);
    borderElement.parentNode.replaceChild(clone, borderElement);
  }
}
